import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, CircularProgress, Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { CampoCorreo, CampoTelefono } from './CamposContacto';
import { errorCorreo, errorTelefono } from '../contacto';
import { BLOQUE, TITULO_BLOQUE } from '../estilos/tarjeta';

export default function Configuracion() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/configuracion/emisor').then(res => setConfig(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleChange = (field) => (e) => setConfig({ ...config, [field]: e.target.value });

  const [intento, setIntento] = useState(false);
  const handleSave = async () => {
    setIntento(true);
    if (errorCorreo(config.email) || errorTelefono(config.telefono)) return;
    setSaving(true);
    setSaved(false);
    try {
      const res = await api.put('/configuracion/emisor', config);
      setConfig(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!config) return <Typography color="error">Error al cargar configuracion</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>Datos del emisor para facturas</Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
          sx={{ bgcolor: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2d5a87' } }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>

      {saved && <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }}>Configuracion guardada correctamente</Alert>}

      {/* Los tipos de orden tienen su propia pantalla (menú Sistema), pero
          quien los busca suele empezar por aquí. */}
      <Alert severity="info" icon={<TuneIcon />} sx={{ mb: 2, borderRadius: 1 }}
        action={<Button size="small" onClick={() => navigate('/tipos-orden')}>Abrir</Button>}>
        <b>Tipos de orden y plantillas</b> (barbería, taller, spa…): qué datos y pasos tienen
        las órdenes de esta empresa.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1 }}>
        {/* Datos de la empresa */}
        <Paper elevation={0} sx={BLOQUE}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <SettingsIcon sx={{ color: '#1e3a5f', fontSize: 17 }} />
            <Typography sx={TITULO_BLOQUE}>Datos del Emisor</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Razon Social" value={config.razon_social || ''} onChange={handleChange('razon_social')} size="small" />
            <TextField label="NIT / RFC" value={config.nit || ''} onChange={handleChange('nit')} size="small" />
            <TextField label="Direccion" value={config.direccion || ''} onChange={handleChange('direccion')} size="small" />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Ciudad" value={config.ciudad || ''} onChange={handleChange('ciudad')} size="small" />
              <TextField label="Pais" value={config.pais || ''} onChange={handleChange('pais')} size="small" />
            </Box>
            <CampoCorreo etiqueta="Correo" valor={config.email || ''} mostrarError={intento}
              alCambiar={(v) => setConfig({ ...config, email: v })} />
            <CampoTelefono etiqueta="Teléfono" valor={config.telefono || ''} mostrarError={intento}
              alCambiar={(v) => setConfig({ ...config, telefono: v })} />
          </Box>
        </Paper>

        {/* Configuracion de facturas */}
        <Paper elevation={0} sx={BLOQUE}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <SettingsIcon sx={{ color: '#1e3a5f', fontSize: 17 }} />
            <Typography sx={TITULO_BLOQUE}>Facturacion</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Prefijo Factura" value={config.prefijo_factura || ''} onChange={handleChange('prefijo_factura')} size="small" />
              <TextField label="Consecutivo Actual" value={config.consecutivo_actual || 0} type="number" size="small" disabled
                helperText="Se incrementa automaticamente" />
            </Box>
            <TextField label="Terminos de Pago" value={config.terminos_pago || ''} onChange={handleChange('terminos_pago')} size="small"
              placeholder="ej: 30 dias" />
            <TextField label="Banco" value={config.banco || ''} onChange={handleChange('banco')} size="small" />
            <TextField label="Cuenta Bancaria" value={config.cuenta_bancaria || ''} onChange={handleChange('cuenta_bancaria')} size="small" />
            <TextField label="Nota al Pie de Factura" value={config.nota_pie_factura || ''} onChange={handleChange('nota_pie_factura')}
              size="small" multiline rows={3} placeholder="Texto legal que aparece al pie de cada factura" />
          </Box>
        </Paper>
      </Box>

      {/* DIAN Facturacion Electronica */}
      <Paper elevation={0} sx={{ ...BLOQUE, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <DescriptionIcon sx={{ color: '#7c3aed', fontSize: 17 }} />
          <Typography sx={TITULO_BLOQUE}>Facturacion Electronica DIAN</Typography>
          <Typography sx={{ fontSize: 11, color: '#7c3aed', bgcolor: '#f3e8ff', px: 1, py: 0.25, borderRadius: 1, fontWeight: 600 }}>Colombia</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField label="NIT Emisor (DIAN)" value={config.dian_nit_emisor || ''} onChange={handleChange('dian_nit_emisor')} size="small"
            placeholder="900123456-1" />
          <TextField label="Ambiente" value={config.dian_ambiente || 'sandbox'} onChange={handleChange('dian_ambiente')} size="small"
            select SelectProps={{ native: true }}>
            <option value="sandbox">Sandbox (Pruebas)</option>
            <option value="produccion">Produccion</option>
          </TextField>
          <TextField label="No. Resolucion" value={config.dian_resolucion_numero || ''} onChange={handleChange('dian_resolucion_numero')} size="small"
            placeholder="18760000001" />
          <TextField label="Fecha Resolucion" value={config.dian_resolucion_fecha || ''} onChange={handleChange('dian_resolucion_fecha')} size="small"
            placeholder="2019-01-19" />
          <TextField label="Prefijo Resolucion" value={config.dian_resolucion_prefijo || ''} onChange={handleChange('dian_resolucion_prefijo')} size="small"
            placeholder="SETT" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Rango Desde" value={config.dian_rango_desde || ''} type="number" onChange={handleChange('dian_rango_desde')} size="small" />
            <TextField label="Rango Hasta" value={config.dian_rango_hasta || ''} type="number" onChange={handleChange('dian_rango_hasta')} size="small" />
          </Box>
          <TextField label="Software ID" value={config.dian_software_id || ''} onChange={handleChange('dian_software_id')} size="small" />
          <TextField label="Software PIN" value={config.dian_software_pin || ''} onChange={handleChange('dian_software_pin')} size="small" type="password" />
        </Box>
      </Paper>
    </Box>
  );
}
